import React, { useState } from 'react';
import { Eye, EyeOff, Phone, Lock, User, ArrowRight, Briefcase, CheckCircle, AlertCircle, X, Shield, Sparkles, MapPin } from 'lucide-react';
import { registerUser, loginUser } from '../../services/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/AuthPage.css';

const accountTypes = [
  { value: 'user', label: 'مستخدم عادي', icon: '🧍‍♂️', description: 'للاستخدام الشخصي' },
  { value: 'restaurant', label: 'مطعم أو كافيه', icon: '🍔', description: 'خدمات الأطعمة والمشروبات' },
  { value: 'store', label: 'بيع أونلاين', icon: '🛍️', description: 'بيع المنتجات أونلاين' },
  { value: 'pharmacy', label: 'صيدلية', icon: '💊', description: 'منتجات طبية ودوائية' },
  { value: 'shop', label: 'محل تجاري', icon: '🏪', description: 'بيع بالتجزئة' },
  { value: 'delivery', label: 'خدمة توصيل', icon: '🚚', description: 'شركة أو مندوب مستقل' },
  { value: 'business', label: 'شركة خدمات', icon: '🏢', description: 'خدمات مؤسسية' },
  { value: 'home_service', label: 'خدمات منزلية', icon: '🏠', description: 'سباكة، تنظيف، صيانة' },
  { value: 'consultant', label: 'استشارات مهنية', icon: '🧾', description: 'محاسبة، قانون، إدارة' },
  { value: 'beauty_center', label: 'مركز تجميل', icon: '💅', description: 'صالون ومراكز عناية' },
  { value: 'entrepreneur', label: 'رائد أعمال', icon: '💼', description: 'مشاريع ريادية' }
];

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showAccountTypes, setShowAccountTypes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: '',
    accountType: 'user',
    rememberMe: false
  });

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 4000);
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return !isLogin && !value.trim() ? 'الرجاء إدخال الاسم' : '';
      case 'address':
        return !isLogin && !value.trim() ? 'الرجاء إدخال العنوان' : '';
      case 'phone':
        if (!value) return 'الرجاء إدخال رقم الهاتف';
        if (!/^01[0125][0-9]{8}$/.test(value)) return 'رقم الهاتف غير صحيح';
        return '';
      case 'password':
        if (!value) return 'الرجاء إدخال كلمة المرور';
        if (value.length < 6) return 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
        return '';
      case 'confirmPassword':
        if (!isLogin && !value) return 'الرجاء تأكيد كلمة المرور';
        if (!isLogin && value !== formData.password) return 'كلمات المرور غير متطابقة';
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      if (key !== 'accountType' && key !== 'rememberMe') {
        const error = validateField(key, formData[key]);
        if (error) newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      if (isLogin) {
        // التحقق من بيانات مدير النظام
        if (formData.phone === '01284845513' && formData.password === 'BOKSHA@zed#128') {
          // توجيه مباشر إلى لوحة التحكم
          window.location.href = 'https://zed-dashboard.web.app';
          return;
        }

        const result = await loginUser(formData.phone, formData.password);
        if (result.status === 'success') {
          showNotification('success', result.message);
          // تحديث حالة المصادقة
          login(result.data.user);
          // التوجيه إلى الصفحة المقصودة أو الرئيسية
          const intendedPath = location.state?.from?.pathname || '/';
          navigate(intendedPath, { replace: true });
        } else {
          showNotification('error', result.message);
        }
      } else {
        const result = await registerUser({
          full_name: formData.name,
          account_type: formData.accountType,
          address: formData.address,
          phone: formData.phone,
          password: formData.password,
          confirm_password: formData.confirmPassword
        });
        showNotification(result.status, result.message);
        if (result.status === 'success') {
          // التبديل إلى نموذج تسجيل الدخول بعد التسجيل الناجح
          setIsLogin(true);
          setFormData({
            ...formData,
            name: '',
            address: '',
            confirmPassword: '',
            accountType: 'user'
          });
        }
      }
    } catch (error) {
      showNotification('error', 'حدث خطأ غير متوقع. الرجاء المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData({ ...formData, [name]: newValue });
    
    if (touched[name]) {
      const error = validateField(name, newValue);
      setErrors({ ...errors, [name]: error });
    }
  };

  const handleBlur = (name) => {
    setTouched({ ...touched, [name]: true });
    const error = validateField(name, formData[name]);
    setErrors({ ...errors, [name]: error });
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setFormData({ name: '', phone: '', password: '', confirmPassword: '', accountType: 'user', rememberMe: false });
    setShowAccountTypes(false);
    setErrors({});
    setTouched({});
  };

  const selectedType = accountTypes.find(type => type.value === formData.accountType);

  return (
    <div className="auth-container">
      {/* Animated Background Elements */}
      <div className="background-animation">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="blob blob-3"></div>
      </div>

      {/* Notification Toast */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.type === 'success' ? <CheckCircle size={22} /> : <AlertCircle size={22} />}
          <span className="notification-message">{notification.message}</span>
          <button 
            onClick={() => setNotification({ show: false, type: '', message: '' })} 
            className="notification-close"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Main Card Container */}
      <div className="main-container mb-96">
        {/* Left Side - Welcome Section */}
        <div className="welcome-section">
          <div className="welcome-overlay"></div>
          <div className="welcome-content">
            <div className="welcome-header">
              <div className="welcome-icon">
                <img src='logo512.png' alt="Logo" />
              </div>
              <h2 className="welcome-title">
                {isLogin ? 'مرحباً بعودتك!' : 'ابدأ رحلتك معنا'}
              </h2>
              <p className="welcome-subtitle">
                {isLogin 
                  ? 'نحن سعداء برؤيتك مرة أخرى. قم بتسجيل الدخول للوصول إلى حسابك.' 
                  : 'انضم إلى آلاف المستخدمين الذين يثقون بنا لإدارة أعمالهم بكفاءة.'}
              </p>
            </div>

            <div className="features-list">
              <div className="feature-item">
                <div className="feature-icon">
                  <CheckCircle size={24} />
                </div>
                <div className="feature-text">
                  <h3>آمن ومحمي</h3>
                  <p>نظام حماية متقدم لبياناتك</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-icon">
                  <Sparkles size={24} />
                </div>
                <div className="feature-text">
                  <h3>واجهة سهلة</h3>
                  <p>تجربة مستخدم سلسة وبديهية</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-icon">
                  <User size={24} />
                </div>
                <div className="feature-text">
                  <h3>دعم مستمر</h3>
                  <p>فريق متاح على مدار الساعة</p>
                </div>
              </div>
            </div>
          </div>

          <div className="welcome-decoration decoration-1"></div>
          <div className="welcome-decoration decoration-2"></div>
        </div>

        {/* Right Side - Form Section */}
        <div className="form-section pb-0 pt-6">
          {/* Mobile Header */}
          <div className="mobile-header">
            <div className="mobile-header-icon">
              <img src='logo512.png' alt="Logo" />
            </div>
            <h1 className="mobile-header-title">
              {isLogin ? 'تسجيل الدخول' : 'حساب جديد'}
            </h1>
          </div>

          {/* Form Container */}
          <div className="form-container">
            {/* Desktop Header */}
            <div className="desktop-header">
              <h2 className="form-title">
                {isLogin ? 'تسجيل الدخول' : 'إنشاء حساب'}
              </h2>
              <p className="form-subtitle">
                {isLogin ? 'أدخل بياناتك للمتابعة' : 'املأ البيانات للبدء'}
              </p>
            </div>

            <div className="form-fields">
              {/* Name Field - Register Only */}
              {!isLogin && (
                <div className="field-group">
                  <label className="field-label">
                    الاسم الكامل <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <div className="input-icon input-icon-right">
                      <User size={20} />
                    </div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      onBlur={() => handleBlur('name')}
                      className={`form-input ${errors.name && touched.name ? 'error' : ''}`}
                      placeholder="أدخل اسمك الكامل"
                    />
                    {errors.name && touched.name && (
                      <p className="error-message">
                        <AlertCircle size={12} />
                        {errors.name}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Account Type - Register Only */}
              {!isLogin && (
                <div className="field-group">
                  <label className="field-label">نوع الحساب</label>
                  <div className="input-wrapper">
                    <div className="input-icon input-icon-right">
                      <Briefcase size={20} />
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAccountTypes(!showAccountTypes)}
                      className="account-type-button"
                    >
                      <span className="account-type-selected">
                        <span className="account-type-icon">{selectedType.icon}</span>
                        <span className="account-type-label">{selectedType.label}</span>
                      </span>
                    </button>
                    
                    {showAccountTypes && (
                      <>
                        <div className="dropdown-overlay" onClick={() => setShowAccountTypes(false)}></div>
                        <div className="account-types-dropdown">
                          {accountTypes.map((type) => (
                            <button
                              key={type.value}
                              type="button"
                              onClick={() => {
                                setFormData({ ...formData, accountType: type.value });
                                setShowAccountTypes(false);
                              }}
                              className={`account-type-item ${formData.accountType === type.value ? 'active' : ''}`}
                            >
                              <span className="account-type-icon">{type.icon}</span>
                              <div className="account-type-info">
                                <div className="account-type-name">{type.label}</div>
                                <div className="account-type-desc">{type.description}</div>
                              </div>
                              {formData.accountType === type.value && (
                                <CheckCircle size={18} className="check-icon" />
                              )}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Address Field - Register Only */}
              {!isLogin && (
                <div className="field-group">
                  <label className="field-label">
                    العنوان <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <div className="input-icon input-icon-right">
                      <MapPin size={20} />
                    </div>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      onBlur={() => handleBlur('address')}
                      className={`form-input ${errors.address && touched.address ? 'error' : ''}`}
                      placeholder="أدخل عنوانك بالتفصيل"
                    />
                    {errors.address && touched.address && (
                      <p className="error-message">
                        <AlertCircle size={12} />
                        {errors.address}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Phone Field */}
              <div className="field-group">
                <label className="field-label">
                  رقم الهاتف <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <div className="input-icon input-icon-right">
                    <Phone size={20} />
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={() => handleBlur('phone')}
                    className={`form-input ${errors.phone && touched.phone ? 'error' : ''}`}
                    placeholder="01xxxxxxxxx"
                    maxLength="11"
                  />
                  {errors.phone && touched.phone && (
                    <p className="error-message">
                      <AlertCircle size={12} />
                      {errors.phone}
                    </p>
                  )}
                </div>
              </div>

              {/* Password Field */}
              <div className="field-group">
                <label className="field-label">
                  كلمة المرور <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <div className="input-icon input-icon-right">
                    <Lock size={20} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={() => handleBlur('password')}
                    className={`form-input password-input ${errors.password && touched.password ? 'error' : ''}`}
                    placeholder="••••••••"
                    style={{ textAlign: 'right' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="input-icon input-icon-left toggle-password"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                  {errors.password && touched.password && (
                    <p className="error-message">
                      <AlertCircle size={12} />
                      {errors.password}
                    </p>
                  )}
                </div>
              </div>

              {/* Confirm Password - Register Only */}
              {!isLogin && (
                <div className="field-group">
                  <label className="field-label">
                    تأكيد كلمة المرور <span className="required">*</span>
                  </label>
                  <div className="input-wrapper">
                    <div className="input-icon input-icon-right">
                      <Lock size={20} />
                    </div>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      onBlur={() => handleBlur('confirmPassword')}
                      className={`form-input password-input ${errors.confirmPassword && touched.confirmPassword ? 'error' : ''}`}
                      placeholder="••••••••"
                      style={{ textAlign: 'right' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="input-icon input-icon-left toggle-password"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                    {errors.confirmPassword && touched.confirmPassword && (
                      <p className="error-message">
                        <AlertCircle size={12} />
                        {errors.confirmPassword}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Remember Me & Forgot Password - Login Only */}
              {isLogin && (
                <div className="login-options">
                  <label className="remember-me">
                    <input 
                      type="checkbox" 
                      name="rememberMe"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                    />
                    <span>تذكرني</span>
                  </label>
                  <button type="button" className="forgot-password">
                    نسيت كلمة المرور؟
                  </button>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="submit-button"
              >
                {loading ? (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <span>جاري التحميل...</span>
                  </div>
                ) : (
                  <>
                    <span>{isLogin ? 'تسجيل الدخول' : 'إنشاء حساب'}</span>
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </div>

            {/* Toggle Mode */}
            <div className="toggle-mode">
              <p>
                {isLogin ? 'ليس لديك حساب؟' : 'لديك حساب بالفعل؟'}
                <button type="button" onClick={toggleMode} className="toggle-button">
                  {isLogin ? 'إنشاء حساب جديد' : 'تسجيل الدخول'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}